import React from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { convertToMoney } from "~/common/utils/currency";
import Carousel from "./Carousel";
import "react-multi-carousel/lib/styles.css";
import Pill from "~/common/ui/Pill";
import useSlugUrl from "~/common/hooks/useSlugUrl";
import { event as gaEvent } from "~/lib/ga";
import useCollectMetrics from "~/common/hooks/useCollectMetrics";
import ContactCard from "./ContactCard/ContactCard";
import CarDetails from "./CarDetails";
import CarVideo from "./CarVideo";
import CarInteractions from "./CarInteractions";
import clsx from "clsx";
import MetadataSection from "./MetadataSection";
import useUser from "~/common/hooks/useUser";
import CardsCotizador from './CardsCotizadorSection';

const Cotizador = dynamic(() => import("./Cotizador"));

const adminOrOwner = ({ user, business, createdBy }) => {
  // Si eres admin pasas de lleno
  if (user.profile.id === 1) {
    return true;
  }
  // Si esta seteado el negocio en el auto, y tu usuario tiene un negocio y ademas ese negocio seteado es el mismo que el del auto entonces pasa.
  if (business && user.business?.id === business.id) {
    return true;
  }

  // Si tu usuario es particular (5) entonces checamos si el auto que estas viendo fue dado de alta por ti y pasaras.
  if (user.profile.id === 5 && createdBy === user.id) {
    return true;
  }

  return false;
};

export default function CarView({
  car,
  owner,
  slug,
  isPreview = false,
  isFinanciable,
  trackEntity,
  financiamientoContact,
  onActionDropdownClick,
}) {
  const { user, isLoading } = useUser();

  const { collectWhatsappButtonClickAction } = useCollectMetrics({
    entity: trackEntity,
  });
  const { path } = useSlugUrl({
    id: car.id,
    slug,
    isPreview,
  });

  const carImages = React.useMemo(() => {
    return [
      {
        id: car.id,
        order: 0,
        urlImage: car.cover,
      },
      ...car.images.map((image, indx) => ({ ...image, order: indx + 1 })),
    ];
  }, [car]);

  const pageUrl = `https://seminuevossonora.com${path}`;

  const handlePlayVideo = () => {
    gaEvent({
      action: "car_play_video",
      params: {
        url: pageUrl,
      },
    });
  };

  const handleWhatsappClick = () => {
    collectWhatsappButtonClickAction(car.id);
  };

  return (
    <>
      {car.status === 4 && (
        <div className="bg-red-500 text-red-800 z-10 -mt-8 p-2 text-center uppercase font-semibold text-xl rounded-b-xl fixed w-full">
          $ Auto vendido $
        </div>
      )}
      {car.status === 2 && (
        <div className="bg-red-500 text-red-800 z-10 -mt-8 p-2 text-center uppercase font-semibold text-xl rounded-b-xl fixed w-full">
          Auto removido
        </div>
      )}

   <div
        className={clsx("w-full flex flex-col", {
          "px-4 md:pl-24 lg:pl-32 2xl:pl-64 md:pr-4 lg:pr-8": !isPreview,
          "mt-8": car.status === 4 || car.status === 2,
        })}
      >
        <div className="flex lg:flex-row flex-col pb-4">
          <div className="lg:w-7/12 w-full">
            <div className="flex justify-between pb-2">
              <div className="flex flex-col gap-1">
                <h1 className="text-3xl font-semibold text-gray-800">
                  {car.title}
                </h1>
                <div className="flex gap-1 flex-wrap">
                  {car.metas.map((meta, idx) => {
                    const { borderless, key, link, value: metaValue } = meta;
                    let value = metaValue;
                    if (key === "km")
                      value = Number(value).toLocaleString("es-MX") + " km";
                    if (link) {
                      return (
                        <Link key={idx} href={link}>
                          <a>
                            <Pill borderless={borderless} key={idx}>
                              {value}
                            </Pill>
                          </a>
                        </Link>
                      );
                    }
                    return (
                      <Pill borderless={borderless} key={idx}>
                        {value}
                      </Pill>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col items-center">
                <h3 className="text-2xl font-semibold text-gray-700">
                  {!car.price
                    ? "Negociable"
                    : convertToMoney(Number(car.price))}
                </h3>
                {car.price && car.negotiable ? <span>Negociable</span> : null}
              </div>
            </div>
            <Carousel images={carImages} />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-5/12 lg:ml-4">
            {!isPreview &&
              !isLoading &&
              user &&
              adminOrOwner({
                user,
                createdBy: car.createdBy,
                business: owner.business,
              }) && (
                <MetadataSection
                  car={car}
                  onActionDropdownClick={onActionDropdownClick}
                />
              )}
            <ContactCard
              owner={owner}
              car={{ id: car.id, type: car.plan, status: car.status, views: car?.views }}
              trackEntity={trackEntity}
            />

            <div className="rounded-lg p-8 border-2 ">
              <CarDetails car={car} />
              <CarInteractions
                onWhatsappClick={handleWhatsappClick}
                pageUrl={pageUrl}
                carYear={car.year}
                ownerWhatsapp={owner.whatsappPhoneNumber}
              />
            </div>
          </div>
        </div>
       {/*
        {!isPreview && isFinanciable && (
          <Cotizador
            year={car.year}
            price={car.price}
            contactNumber={financiamientoContact}
            url={pageUrl}
          />
        )}  
        */}
        {!isPreview && car.year >= 2017 && Number(car.price) > 0 && (
          <CardsCotizador key={car.id} auto={car} slug={slug} />
        )}
        <div className="py-4">
          <div className="w-full">
            <div className="bg-brand-gray h-0.5 w-full"></div>
            <div className="w-full flex items-center justify-center -mt-2">
              <p className="top-1.5 bg-white text-gray-400 underline-offset-2 underline  decoration-brand-primary decoration-2 px-2 font-semibold -mt-1 tracking-wide">
                Detalles
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-4 flex-col lg:flex-row justify-between pb-6">
          <div
            className="lg:w-2/4"
            dangerouslySetInnerHTML={{ __html: car.description }}
          ></div>

          {car.video && car.video.includes("https") && (
            <div className="lg:w-2/4">
              <CarVideo
                video={car.video}
                muted={!car.isNew}
                onPlay={handlePlayVideo}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
